import _ from 'underscore';
import { CivilDate } from 'tc39-proposal-temporal';

export const MAX_VALUE = 8640000000000000;
export const MIN_VALUE = -8640000000000000;

// Returns a date that is the first day of the month of the provided date.
export function getCivilDateMonthStart(date) {
  return new CivilDate(date.year, date.month, 1);
}

// Returns a date that is the last day of the month of the provided date.
export function getCivilDateMonthEnd(date) {
  return new CivilDate(date.year, date.month + 1, 0);
}

// Checks whether the date is valid.
export const isValidDate = value => _.isDate(value) && !_.isNaN(value.getTime());

// Get a Civil Date from Date object
export const getCivilDateFromDate = value => (isValidDate(value) ? new CivilDate(
  value.getFullYear(),
  value.getMonth() + 1,
  value.getDate()
) : null);

export const maxCivilDate = getCivilDateFromDate(new Date(MAX_VALUE));
export const minCivilDate = getCivilDateFromDate(new Date(MIN_VALUE));

// Compare the month parts of two dates. A negative value if date1 is earlier than date2,
// 0 if the dates are equal, or a positive value if date1 is later than date2.
export function compareCivilMonth(date1, date2) {
  if (date1.year !== date2.year) {
    return date1.year - date2.year;
  }
  return date1.month - date2.month;
}

// Compare the date parts of two dates. A negative value if date1 is earlier than date2,
// 0 if the dates are equal, or a positive value if date1 is later than date2.
export function compareCivilDate(date1, date2) {
  if (date1.year !== date2.year) {
    return date1.year - date2.year;
  } else if (date1.month !== date2.month) {
    return date1.month - date2.month;
  }
  return date1.day - date2.day;
}

// Checks whether the specified date is in the given date range.
export function isCivilDateInRange(date, { start, end }) {
  if (start && end) {
    if ((compareCivilDate(start, date) >= 0 && compareCivilDate(end, date) <= 0)
      || (compareCivilDate(start, date) <= 0 && compareCivilDate(end, date) >= 0)) {
      return true;
    }
  }
  return false;
}

// Checks whether the specified date is in the given date open range
// eslint-disable-next-line max-len
export const isCivilDateInOpenRange = ({ minDate = minCivilDate, maxDate = maxCivilDate, value }) => {
  if (!value) {
    return false;
  }
  return isCivilDateInRange(value, { start: minDate, end: maxDate });
};

// Checks whether the month of the specified date is in the given date range.
export function isCivilMonthInRange(date, { start, end }) {
  if (start && end) {
    return (compareCivilMonth(start, date) >= 0 && compareCivilMonth(end, date) <= 0)
    || (compareCivilMonth(start, date) <= 0 && compareCivilMonth(end, date) >= 0);
  }
  return false;
}

// TODO: remove it and use civildate own function to do this.
// There is a issue tracking this function: https://github.com/tc39/proposal-temporal/issues/69
export function getCivilDateDayOfWeek(civilDate) {
  return (new Date(civilDate.year, civilDate.month - 1, civilDate.day)).getDay();
}

// get a date that is the monday of this week(monday - sunday)
export const getThisMonday = date => (getCivilDateDayOfWeek(date) === 0 ?
  date.plus({ days: -6 }) :
  date.plus({ days: -getCivilDateDayOfWeek(date) + 1 }));

export function getCivilDateToday() {
  return getCivilDateFromDate(new Date());
}

// Checks whether the specified date is the the given date range start or end.
export function isCivilDateRangeStartOrEnd(date, dateRange) {
  return _.some(dateRange, d => compareCivilDate(date, d) === 0);
}
